import { Schema } from "effect"
import { AgentKind, Effort, Evidence, Hold, Principal, RecordMeta, WorkRef } from "../common"
import {
  AgentId,
  AreaId,
  CommentId,
  EdgeId,
  InitiativeId,
  IntegrationId,
  IntentKey,
  Millis,
  ProfileId,
  ProposalId,
  RunId,
  Slug,
  TaskId,
  UserId,
} from "../ids"
import { Placement } from "../placement"
import { MetricCondition, NodeBase } from "./plan"

// #region task
export const ArtifactKind = Schema.Literals([
  "pull_request",
  "branch",
  "commit",
  "file",
  "document",
  "page_draft",
  "image",
  "report",
  "dataset",
  "build",
  "link",
  "log",
  // company-wide content (decision 0003): editorial and social work products
  "article",
  "video",
  "script",
  "brief",
])

/**
 * A check that decides "done". Written before work starts, frozen when the first attempt starts, read-only to
 * workers. Checks are instructions and evidence requirements, never shell verbs in a job: a `command` check is
 * run by the worker and re-run by the validator inside their own CLI sessions, under their permission profile.
 */
export const DoneCheck = Schema.TaggedUnion({
  command: { name: Schema.String, description: Schema.String }, // "pnpm test", "lighthouse ≥ 90"
  artifact: { kind: ArtifactKind, description: Schema.String }, // an artifact of this kind must be posted
  rubric: { criteria: Schema.NonEmptyArray(Schema.String) }, // judged by a validator, cross-model by default
  human: { approverId: Schema.NullOr(UserId), prompt: Schema.String }, // null → review.escalateTo
  metric: { condition: MetricCondition, afterDays: Schema.Int }, // outcome check (Phase 3); never gates `done`
})

export const Contract = Schema.Struct({
  checks: Schema.Array(DoneCheck), // ≥ 1 required before the task can be ready (planner validator)
  frozenAt: Schema.NullOr(Millis), // set when the first attempt starts; later changes need a human-approved proposal
})

/** Verification ladder stages, in order. `agent` = the assignee reviews; `validator` = a separate run;
 * `human` = a question proposal to `escalateTo` (or the check's approver). */
export const ReviewStage = Schema.Literals(["agent", "validator", "human"])
export const ReviewConfig = Schema.Struct({
  stages: Schema.NonEmptyArray(ReviewStage), // default ["agent"]; Phase 2 adds "validator"
  crossModel: Schema.Boolean, // validator runs on the other model family (default true)
  maxRounds: Schema.Int, // 2–3; then escalate to a person
  escalateTo: UserId,
})
export const Review = Schema.Struct({
  ...ReviewConfig.fields,
  round: Schema.Int, // 0 before the first review
  stage: Schema.Int, // index into stages for the current round
})

/** What a worker or agent reports as the obstacle. */
export const Blocker = Schema.TaggedUnion({
  needs_input: { from: Schema.Literals(["assignee", "lead", "human"]), what: Schema.String },
  missing_access: { what: Schema.String }, // integration grant, repo, secret
  environment_broken: { what: Schema.String }, // build env, simulator, CI
  depends_on: { taskId: TaskId }, // the assignee should add a blocks edge
  other: { what: Schema.String },
})

export const ImpossibleReason = Schema.Literals([
  "spec_conflict",
  "contract_unreachable",
  "premise_false",
  "out_of_scope",
])

/** Why a task is `blocked`. Every human-needing block has exactly one escalation in the ledger. */
export const BlockReason = Schema.TaggedUnion({
  needs_human: { proposalId: ProposalId }, // non-retryable stop, repeated failure, review rounds exhausted, …
  question: { askId: CommentId }, // waiting for an agent's answer (ask goes one level up)
  reported: { blocker: Blocker, runId: Schema.NullOr(RunId) }, // the assignee resolves it
  impossible_claimed: { runId: RunId, reason: ImpossibleReason }, // the assignee accepts it or revises the contract
})

export const TaskStatus = Schema.Literals([
  "todo", // not started, or between attempts. Readiness is DERIVED, never stored.
  "in_progress", // an attempt (run) is live
  "in_review", // a done claim was accepted; review stages are running
  "blocked", // explicit, with a BlockReason; never auto-retried
  "done", // contract satisfied with evidence, through the review stages
  "impossible", // accepted spec-conflict exit: a good outcome
  "cancelled",
])
export type TaskStatus = typeof TaskStatus.Type

export const TaskParent = Schema.TaggedUnion({ initiative: { id: InitiativeId }, task: { id: TaskId } })

/** Worker defaults: which CLI family, model and effort. Agents set defaults; tasks override. */
export const Hands = Schema.Struct({
  agent: Schema.NullOr(AgentKind),
  model: Schema.NullOr(Schema.String),
  effort: Schema.NullOr(Effort),
})

/**
 * Who executes a task (settings wave S14, decision 0028). Absent on a task = worker{preset: null}. `agent` is reserved:
 * decide rejects it this wave (a persistent agent doing a task in its own turns comes later).
 */
export const TaskExecutor = Schema.TaggedUnion({
  /** A temporary worker with the named preset of the project; null = the project's default preset. */
  worker: { preset: Schema.NullOr(Slug) },
  agent: { agentId: AgentId },
})
export type TaskExecutor = typeof TaskExecutor.Type

export const Task = Schema.Struct({
  ...NodeBase,
  id: TaskId,
  /** `publish` is the outward step: the only kind that may hold outward_action proposals' executions. */
  kind: Schema.Literals(["work", "publish"]),
  parent: TaskParent,
  initiativeId: InitiativeId, // root initiative (denormalised, immutable)
  depth: Schema.Literals([0, 1, 2]), // 0 = directly under the initiative; at most two subtask levels
  areaId: AreaId, // executing area; differs from the initiative's for cross-area requests
  /** The persistent agent responsible; workers are runs, never assignees. Human assignees (decision 0003 #5) arrive
   *  as an additive field (a user principal), so today's TaskCreated/TaskAssigned payloads stay valid. */
  assigneeId: Schema.NullOr(AgentId),
  status: TaskStatus,
  blocked: Schema.NullOr(BlockReason), // non-null iff status = blocked
  paused: Schema.Boolean, // human hold: the scheduler skips it
  /** Who paused it (decision 0032): a person's pause is never resumed by an agent. null = the system (e.g. a stop
   *  with no recorded requester) or not paused; absent = paused before this field existed (treated as null). */
  pausedBy: Schema.optionalKey(Schema.NullOr(Principal)),
  pauseNote: Schema.optionalKey(Schema.NullOr(Schema.String)),
  /** The evidence of a CompleteTask, kept while the task passes its later review stages (cleared by a new attempt). */
  closeEvidence: Schema.optionalKey(Schema.Array(Evidence)),
  /** Meaning-based dedupe: one open task per (parent, intentKey, assignee). */
  intentKey: IntentKey,
  contract: Contract,
  review: Review,
  placement: Placement, // task overrides; merged over area defaults when a run is created, then frozen on the run
  hands: Hands,
  paths: Schema.Array(Schema.String), // memory triggers ("/compare/**")
  integrations: Schema.Array(IntegrationId), // needed grants; must be a subset of the area's grants
  /** Cross-area request: the receiving area's intake agent accepts or declines (a graph edit, not chat). Not to be
   *  confused with an Intake item (domain/intake.ts), which is captured input waiting for triage. */
  request: Schema.NullOr(
    Schema.Struct({ fromAreaId: AreaId, state: Schema.Literals(["pending", "accepted", "declined"]) }),
  ),
  attempts: Schema.Int, // worker attempts started
  /** Set by a retry decision: the next attempt waits until `at`; rate_limited pins the same profile. */
  retry: Schema.NullOr(Schema.Struct({ at: Millis, pinProfile: Schema.NullOr(ProfileId) })),
  activeRunId: Schema.NullOr(RunId),
  lastRunId: Schema.NullOr(RunId),
  supersedes: Schema.NullOr(TaskId),
  /** Settings wave: who executes it. decide writes it on every new task; absent on older tasks = worker{preset: null}. */
  executor: Schema.optionalKey(TaskExecutor),
})
export type Task = typeof Task.Type
// #endregion task

// #region edge
/**
 * Three edge kinds. `blocks`: `to` is not ready until `from` is done (drives the ready frontier); a `blocks` edge
 * into an initiative or task gates all of its descendants. `informs`: `to`'s context gets `from`'s artifacts by
 * reference (soft; area interfaces). `spawned_from`: provenance only (`to` exists because of `from`, e.g. a
 * trigger or a follow-up); it never gates. Parent/child is a field, KPI roll-up is a field, "executed by" is
 * derived from runs, human gates are `proposal.blocks`.
 */
export const EdgeKind = Schema.Literals(["blocks", "informs", "spawned_from"])
export const Edge = Schema.Struct({
  ...RecordMeta,
  id: EdgeId,
  kind: EdgeKind,
  from: WorkRef,
  to: WorkRef,
  interfaceKey: Schema.NullOr(Slug), // informs: which published area interface flows along it
  note: Schema.NullOr(Schema.String),
  hold: Hold, // a person can lock a blocks edge so agents can't cut it
})
// #endregion edge
